import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import SingleProductCTA from '../../../features/purchaseMotions/components/SingleProductCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { isUngatedForM2BundlePagesSelector } from '../../../selectors/GatingSelectors';
import { isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { BUNDLE_FEATURE_TABLE_HEADER } from '../../../v2/constants/tracking/TrackingSources';
import { PriceByProductApiName } from '../../../v2/containers/PriceByProductApiName';
import { PurchaseMotionContainer } from '../../../v2/containers/PurchaseMotionContainer';
import { isFreeProduct } from '../../../v2/utils/highlightedProductUtils';
import { createFeatureTableHeaderTrackingData } from '../../../v2/utils/trackingData';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import FeatureTableColTag from '../FeatureTableColTag';
import FreeProductFeatureTableHeaderCell from '../product-feature-table/FreeProductFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GenericBundleFeatureTableHeaderCell = ({
  trackingSource = BUNDLE_FEATURE_TABLE_HEADER,
  screen,
  apiName,
  badgeName
}) => {
  const isAppReady = useGlobalLoading();
  const isFree = isFreeProduct(apiName);
  const product = useAppSelector(state => {
    return productSelector(state, {
      apiName
    });
  });
  const isMerchCardCTAsDataLoading = useAppSelector(isMerchCardCTAsDataLoadingSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    purchaseOptionsForTerm: pricingInfo,
    loading: pricingInfoLoading
  } = usePurchaseableProductsForTerm({
    apiName,
    queryParamTerm
  });
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId: product === null || product === void 0 ? void 0 : product.currentSkuId
  });
  const isM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const loading = pricingInfoLoading || !isAppReady;
  if (isFree) {
    return /*#__PURE__*/_jsx(FreeProductFeatureTableHeaderCell, {
      apiName: apiName,
      badgeName: badgeName,
      screen: screen,
      trackingSource: trackingSource
    });
  }
  if (!product && !loading) return null;
  const buttonVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header'
  });
  let PriceComponent = null;
  if (isM2BundlePages) {
    PriceComponent = /*#__PURE__*/_jsx(PriceByProductApiName, {
      productApiName: apiName
    });
  } else if (pricingInfo) {
    PriceComponent = /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
      pricingInfo: pricingInfo,
      seleniumId: apiName || 'loading',
      currencyCode: currencyCode,
      currencyCodeUnit: currencyCodeUnit
    });
  }
  let PurchaseMotionComponent = null;
  if (isM2BundlePages) {
    PurchaseMotionComponent = /*#__PURE__*/_jsx(PurchaseMotionContainer, {
      productApiName: apiName,
      location: "MERCH_CARD",
      displayCount: 1,
      trackingData: createFeatureTableHeaderTrackingData(apiName, screen)
    });
  } else {
    PurchaseMotionComponent = /*#__PURE__*/_jsx(SingleProductCTA, Object.assign({
      screen: screen,
      apiName: apiName,
      trackingSource: trackingSource,
      purchaseMotion: purchaseMotions[0],
      purchaseMotionLocation: 'GENERIC'
    }, buttonVariantProps));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      badgeName: badgeName
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `products.${apiName}`
      })
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [140],
        children: PriceComponent
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isMerchCardCTAsDataLoading,
      heights: [40],
      widths: [190],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: apiName ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: trackingSource,
            screen: screen
          }) : null,
          children: PurchaseMotionComponent
        })
      }))
    })]
  });
};
export default GenericBundleFeatureTableHeaderCell;